<template>
    <div class="flex-none p-4 bg-white rounded-lg shadow-md">
        <nav aria-label="Progress" class="sticky top-20 w-full max-w-64">
            <div class="container">
                <div class="flex flex-col">
                    <h2 class="text-xl font-bold leading-tight">Application Progress</h2>
                    <h4 class="text-sm font-semibold">{{ progress.completed_steps }} of {{ progress.total_steps }} steps completed</h4>
                </div>
                <ol role="list" class="overflow-hidden mt-6">
                    <li v-for="(step, stepName, index) in stepStatus" :key="stepName" :class="[index !== Object.keys(stepStatus).length - 1 ? 'pb-10' : '', 'relative']">
                        <template v-if="step.is_completed">
                          <div v-if="index !== Object.keys(stepStatus).length - 1" class="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-green-600" aria-hidden="true" />
                            <div class="group relative flex items-start w-full text-left p-2 rounded -m-2 cursor-not-allowed">
                                <span class="flex h-9 items-center">
                                  <span class="relative z-10 flex size-8 items-center justify-center rounded-full bg-green-600">
                                      <font-awesome-icon :icon="faCheck" class="text-white" aria-hidden="true" />
                                  </span>
                                </span>
                                <span class="ml-4 flex min-w-0 flex-col">
                                  <span class="text-sm font-bold text-gray-600">{{ getStepDisplayName(stepName).stepNumber }} {{ getStepDisplayName(stepName).displayName }}</span>
                                  <span class="text-sm text-gray-500">{{ getStepDisplayName(stepName).description }}</span>
                                </span>
                            </div>
                        </template>
                        <template v-else-if="step.is_current">
                        <div v-if="index !== Object.keys(stepStatus).length - 1" class="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300" aria-hidden="true" />
                        <!-- IDV Special Handling -->
                        <button 
                            v-if="stepName === 'identity_verification'"
                            @click="handleIdvClick"
                            class="group relative flex items-start w-full text-left hover:bg-gray-50 p-2 rounded -m-2"
                            :class="{ 'active': isCurrentStep(stepName) }"
                            aria-current="step"
                            :disabled="isLoader"
                        >
                            <span class="flex h-9 items-center" aria-hidden="true">
                            <span class="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-blue-600 bg-white">
                                <span v-if="isLoader" class="animate-spin">⟳</span>
                                <span v-else class="size-2.5 rounded-full bg-blue-600" />
                            </span>
                            </span>
                            <span class="ml-4 flex min-w-0 flex-col">
                            <span class="text-sm font-bold text-gray-600" :class="{ 'text-indigo-600': isCurrentStep(stepName) }">{{ getStepDisplayName(stepName).stepNumber }} {{ getStepDisplayName(stepName).displayName }}</span>
                            <span class="text-sm text-gray-500">{{ getStepDisplayName(stepName).description }}</span>
                            </span>
                        </button>
                        <!-- Regular Link for other steps -->
                        <Link 
                            v-else
                            :href="getStepRoute(stepName)"
                            class="group relative flex items-start w-full text-left hover:bg-gray-50 p-2 rounded -m-2"
                            :class="{ 'active': isCurrentStep(stepName) }"
                            aria-current="step"
                        >
                            <span class="flex h-9 items-center" aria-hidden="true">
                            <span class="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-blue-600 bg-white">
                                <span class="size-2.5 rounded-full bg-blue-600" />
                            </span>
                            </span>
                            <span class="ml-4 flex min-w-0 flex-col">
                            <span class="text-sm font-bold text-gray-600" :class="{ 'text-indigo-600': isCurrentStep(stepName) }">{{ getStepDisplayName(stepName).stepNumber }} {{ getStepDisplayName(stepName).displayName }}</span>
                            <span class="text-sm text-gray-500">{{ getStepDisplayName(stepName).description }}</span>
                            </span>
                        </Link>
                        </template>
                        <template v-else>
                        <div v-if="index !== Object.keys(stepStatus).length - 1" class="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300" aria-hidden="true" />
                        <div class="group relative flex items-start cursor-not-allowed">
                            <span class="flex h-9 items-center" aria-hidden="true">
                              <span class="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white">
                                <span class="size-2.5 rounded-full bg-transparent " />
                              </span>
                            </span>
                            <span class="ml-4 flex min-w-0 flex-col">
                            <span class="text-sm font-bold text-gray-400">{{ getStepDisplayName(stepName).stepNumber }} {{ getStepDisplayName(stepName).displayName }}</span>
                            <span class="text-sm text-gray-400">{{ getStepDisplayName(stepName).description }}</span>
                            </span>
                        </div>
                        </template>
                    </li>
                </ol>
                <!-- Legend for step status -->
                <div class="mt-6 pt-4 border-t border-gray-200">
                    <h3 class="text-xs font-semibold text-gray-600 mb-2">Status:</h3>
                    <div class="space-y-1.5 text-xs">
                        <div class="flex items-center text-gray-600">
                            <span class="flex h-3 w-3 items-center justify-center rounded-full bg-green-600 mr-2">
                                <font-awesome-icon :icon="faCheck" class="text-white text-[8px]" />
                            </span>
                            <span>Completed</span>
                        </div>
                        <div class="flex items-center text-gray-600">
                            <span class="flex h-3 w-3 items-center justify-center rounded-full border-2 border-blue-600 bg-white mr-2">
                                <span class="h-1 w-1 rounded-full bg-blue-600"></span>
                            </span>
                            <span>Available / Next</span>
                        </div>
                        <div class="flex items-center text-gray-600">
                            <span class="flex h-3 w-3 items-center justify-center rounded-full border-2 border-gray-300 bg-white mr-2">
                                <span class="h-1 w-1 rounded-full bg-transparent"></span>
                            </span>
                            <span>Locked / Remaining</span>
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    </div>
</template>

<script>

    /* import font awesome icon component */
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
    /* import specific icons */
    import {
        faCheck,
    } from '@fortawesome/pro-solid-svg-icons'
    /* import Inertia Link component */
    import { Link } from '@inertiajs/vue3'
    /* import axios for API calls */
    import axios from 'axios'
    /* import router for navigation */
    import { router } from '@inertiajs/vue3'

  export default {
    components: {
        FontAwesomeIcon,
        Link
    },

    props: {
      application: {
        type: Object,
        default: () => ({})
      },
      property: {
        type: Object,
        default: () => ({})
      },
      stepCompletionStatus: {
        type: Object,
        default: () => ({})
      },
      stepProgressData: {
        type: Object,
        default: () => ({})
      },
      completedStepsMap: {
        type: Object,
        default: () => ({})
      },
      currentStep: {
        type: String,
        default: 'dashboard'
      }
    },
    emits: ['navigate'],
    data() {
        return {
            faCheck,
            isLoader: false
        }
    },
    computed: {
      stepStatus() {
        return this.stepCompletionStatus || {};
      },
      progress() {
        return this.stepProgressData || {};
      },
      // Generate step numbers dynamically based on actual steps shown
      // Order: core steps through financial docs, then ESO add-ons, then share_report last
      dynamicStepNumbers() {
        const stepOrder = [
          'terms_and_conditions',
          'payment',
          'personal_information',
          'credit_background_report',
          'financial_documents',
          'identity_verification',
          'income_verification',
          'assets',
          'payroll',
          'share_report'
        ];
        
        let stepNumber = 1;
        const numbers = {};
        
        stepOrder.forEach(stepName => {
          // Only assign a number if this step exists in stepStatus
          if (this.stepStatus[stepName]) {
            numbers[stepName] = stepNumber++;
          }
        });
        
        return numbers;
      }
    },
    methods: {
      getStepDisplayName(stepName) {
        const names = {
          'terms_and_conditions': {
            displayName: 'Terms and Conditions',
            description: 'Please read and accept the terms and conditions before proceeding.'
          },
          'payment': {
            displayName: 'Payment',
            description: 'Purchase the report to proceed with the application.'
          },
          'personal_information': {
            displayName: 'Personal Information',
            description: 'Please provide your personal information to proceed with the application.'
          },
          'credit_background_report': {
            displayName: 'Credit & Background Report',
            description: 'Review your credit and background report.'
          },
          'financial_documents': {
            displayName: 'Financial Documents',
            description: 'Please provide your financial documents to proceed with the application.'
          },
          'identity_verification': {
            displayName: 'Identity Verification',
            description: 'Please provide your identity verification documents to proceed with the application.'
          },
          'income_verification': {
            displayName: 'Income Verification',
            description: 'Please provide your income verification documents to proceed with the application.'
          },
          'assets': {
            displayName: 'Assets Verification',
            description: 'Please provide your assets verification documents to proceed with the application.'
          },
          'payroll': {
            displayName: 'Employment Verification',
            description: 'Please provide your employment verification documents to proceed with the application.'
          },
          'share_report': {
            displayName: 'Share Report',
            description: 'Please share your report with your Agent/Landlord.'
          },
        };
        
        // Get the dynamic step number for this step
        const stepNumber = this.dynamicStepNumbers[stepName] ? `${this.dynamicStepNumbers[stepName]}.` : '';
        
        const stepInfo = names[stepName] || { 
          displayName: stepName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          description: ''
        };
        
        return {
          stepNumber: stepNumber,
          displayName: stepInfo.displayName,
          description: stepInfo.description
        };
      },
      getStepRoute(stepName) {
        if(stepName === 'identity_verification') {
          return 'createLinkToken';
        }
        // Map step names to routes
        const routeMap = {
          'terms_and_conditions': this.route('application.terms-and-conditions', this.application.id),
          'payment': this.route('application.payment', this.application.id),
          'personal_information': this.route('application.personal-information', this.application.id),
          'credit_background_report': this.route('application.credit-report', this.application.id),
          'financial_documents': this.route('application.financial-documents', this.application.id),
          'income_verification': this.route('user-incomes.application', this.application.id),
          'assets': this.route('user-assets.application', this.application.id),
          'payroll': this.route('user-employment-verification.application', this.application.id),
          'share_report': this.route('application.share', this.application.id)
        };
        
        return routeMap[stepName] || this.route('application.steps', this.application.id);
      },
      navigateToStep(stepName) {
        // This method is kept for backward compatibility but no longer used
        this.$emit('navigate', stepName);
      },
      isCurrentStep(stepName) {
        return this.currentStep === stepName || 
               (this.currentStep === 'dashboard' && stepName === 'terms_and_conditions');
      },
      
      // IDV Handling Methods
      async handleIdvClick() {
        if (this.isLoader) return;
        
        this.isLoader = true;
        try {
          // Set up axios headers
          // CSRF handling is now managed globally via axios defaults and XSRF cookies
          
          // Get the addon_id from the step data
          const stepData = this.stepStatus['identity_verification'];
          const addonId = stepData.addon_id;
          
          // Create action object for IDV
          const action = {
            addon: {
              unique_name: 'identity_verification',
              id: addonId
            }
          };
          
          // Call createLinkToken method
          await this.createLinkToken(action);
          
        } catch (error) {
          console.error('Error:', error.response ? error.response.data : error.message);
          this.isLoader = false;
        }
      },
      
      async createLinkToken(action) {
        try {
          // Ensure CSRF cookie exists immediately before posting
          await axios.get('/sanctum/csrf-cookie');
          const response = await axios.post(route('plaid.create-link-token'), {
            product: action.addon.unique_name,
          });
          
          if (response.data.status) {
            this.initializePlaidLink(response.data.public_token, action.addon);
          }
        } catch (error) {
          console.error('Error creating link token:', error.response ? error.response.data : error.message);
          this.isLoader = false;
        }
      },
      
      async initializePlaidLink(public_token, product) {
        this.isLoader = false;
        
        const handler = Plaid.create({
          token: public_token,
          onLoad: function () {
            // Optional, called when Link loads
          },
          onSuccess: (public_token, metadata) => {
            this.isLoader = true;
            if (product.unique_name === 'identity_verification') {
              this.finishIdv(public_token, metadata, product);
            }
          },
          onExit: function (err, metadata) {
            // The user exited the Link flow
            if (err != null) {
              // The user encountered a Plaid API error prior to exiting
              console.error('Plaid Link error:', err);
            }
          },
          onEvent: function (eventName, metadata) {
            // Optionally capture Link flow events
            console.log('Plaid event:', eventName, metadata);
          }
        });
        
        handler.open();
      },
      
      async finishIdv(public_token, metadata, product) {
        try {
          const response = await axios.post(route('plaid.idv.submit.application'), {
            public_token: public_token,
            metadata: metadata,
            application_id: this.application.id,
            addon_id: product.id, // This now contains the correct addon_id
          });
          
          this.isLoader = false;
          
          // Reload the page to update the application state
          location.reload();
        } catch (error) {
          console.error('Error finishing IDV:', error.response ? error.response.data : error.message);
          this.isLoader = false;
        }
      }
    }
  }
  </script>